import { Check, Copy } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import type { PaymentMethodDto } from '@entities/deposit';
import { haptics } from '@shared/lib/telegram';
import styles from './PaymentMethods.module.css';

const SMOOTH_SPRING = { type: 'spring', stiffness: 300, damping: 30, mass: 0.85 } as const;
const IOS_EASE = [0.32, 0.72, 0, 1] as const;

/** Compact payment choices with one shared details card below the grid. */
export function PaymentMethods({ methods }: { methods: PaymentMethodDto[] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const selected = methods.find((method) => method.id === selectedId) ?? null;

  const copy = async (method: PaymentMethodDto) => {
    try {
      await navigator.clipboard.writeText(method.accountNumber);
      haptics.notify('success');
      setCopyFailed(false);
      setCopiedId(method.id);
      window.setTimeout(
        () => setCopiedId((current) => (current === method.id ? null : current)),
        1800,
      );
    } catch {
      haptics.notify('error');
      setCopyFailed(true);
    }
  };

  if (methods.length === 0) {
    return <p className={styles.empty}>No payment accounts are available right now.</p>;
  }

  return (
    <>
      <div className={styles.grid}>
        {methods.map((method) => {
          const isSelected = selectedId === method.id;

          return (
            <motion.button
              key={method.id}
              id={`payment-method-${method.id}`}
              type="button"
              className={[styles.method, isSelected ? styles.selected : '']
                .filter(Boolean)
                .join(' ')}
              aria-pressed={isSelected}
              aria-controls="payment-method-details"
              whileTap={{ scale: 0.985 }}
              transition={SMOOTH_SPRING}
              onClick={() => {
                haptics.select();
                setCopyFailed(false);
                setSelectedId((current) => (current === method.id ? null : method.id));
              }}
            >
              <img
                className={styles.logo}
                src={method.logoUrl}
                alt=""
                width={42}
                height={42}
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
              <span className={styles.name}>{method.name}</span>
              <motion.span
                className={styles.selectionIndicator}
                animate={{ scale: isSelected ? 1 : 0.82, opacity: isSelected ? 1 : 0 }}
                transition={SMOOTH_SPRING}
                aria-hidden="true"
              >
                <Check size={12} weight="bold" />
              </motion.span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence initial={false}>
        {selected ? (
          <motion.div
            id="payment-method-details"
            className={styles.detailsViewport}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.38, ease: IOS_EASE },
              opacity: { duration: 0.26, ease: IOS_EASE },
            }}
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={selected.id}
                className={styles.details}
                role="region"
                aria-labelledby={`payment-method-${selected.id}`}
                initial={{ opacity: 0, y: 6, scale: 0.995 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.995 }}
                transition={{ duration: 0.26, ease: IOS_EASE }}
              >
                <div className={styles.detailsHeader}>
                  <img
                    className={styles.detailsLogo}
                    src={selected.logoUrl}
                    alt=""
                    width={34}
                    height={34}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                  <div>
                    <span>Selected payment account</span>
                    <strong>{selected.name}</strong>
                  </div>
                </div>

                <div className={styles.accountRow}>
                  <div className={styles.accountBlock}>
                    <span className={styles.detailLabel}>Account number</span>
                    <span className={styles.account}>{selected.accountNumber}</span>
                  </div>
                  <button
                    type="button"
                    className={copiedId === selected.id ? styles.copyButtonDone : styles.copyButton}
                    onClick={() => void copy(selected)}
                  >
                    {copiedId === selected.id ? (
                      <>
                        <Check size={15} weight="bold" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={15} weight="bold" /> Copy
                      </>
                    )}
                  </button>
                </div>

                <p className={styles.holder}>
                  <span>Account name</span>
                  <strong>{selected.accountName}</strong>
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {copyFailed ? (
        <p className={styles.copyError} role="status">
          Couldn’t copy automatically. Press and hold the account number to copy it.
        </p>
      ) : null}
    </>
  );
}
